import {AxiosResponse} from 'axios'
import {RawPagedCollectionResponse} from '../types'
import {HttpInstance} from '../createHttpInstance'

/**
 * Bunk assignments — which people sit in which bunk, for a bunk plan.
 *
 * Path and response shape confirmed against the bunks service's own generated
 * spec (`bunks` repo, `gen/docs/external/openapi.yaml`, operation
 * `Assignments`).
 *
 * BOTH `bunkplanids` and `bunkids` are declared required there: a bunk plan id
 * on its own is not a valid request. The bunk ids are the selected plan's own
 * `BunkIDs`, which the bunk-plans list already returns, so no extra lookup is
 * needed to satisfy it.
 *
 * Results are grouped BY BUNK, not by person — one row per bunk carrying its
 * assignment list — so callers have to flatten to get a per-camper answer.
 */
interface Params {
  ClientID: number
  SeasonID: number
  BunkPlanIDs: number[]
  BunkIDs: number[]
  PageNumber: number
  PageSize: number
  IncludeDeleted?: boolean
}

export interface RawBunkAssignment {
  ID: number
  PersonID: number
  LastUpdatedUTC: string
  IsDeleted: boolean
}

export interface RawBunkAssignments {
  ClientID: number
  BunkPlanID: number
  BunkID: number
  Assignments: RawBunkAssignment[] | null
}

export type ListBunkAssignmentsResponse =
  RawPagedCollectionResponse<RawBunkAssignments>

export interface ListBunkAssignments<P, R> {
  (http: HttpInstance, params: P): Promise<AxiosResponse<R>>
}

export const listBunkAssignments: ListBunkAssignments<
  Params,
  ListBunkAssignmentsResponse
> = (http, params) => {
  const {
    ClientID,
    SeasonID,
    BunkPlanIDs,
    BunkIDs,
    PageNumber,
    PageSize,
    IncludeDeleted,
  } = params

  const queryParams: string[] = []
  queryParams.push(`clientid=${ClientID}`)
  queryParams.push(`seasonid=${SeasonID}`)
  queryParams.push(`pagenumber=${PageNumber}`)
  queryParams.push(`pagesize=${PageSize}`)
  if (IncludeDeleted !== undefined) {
    queryParams.push(`includedeleted=${IncludeDeleted}`)
  }
  BunkPlanIDs.forEach((id) => queryParams.push(`bunkplanids=${id}`))
  BunkIDs.forEach((id) => queryParams.push(`bunkids=${id}`))

  const query = `?${queryParams.join('&')}`

  return http.get<ListBunkAssignmentsResponse>(`/v0/bunks/assignments${query}`)
}
